/*
 * Copyright (c) 2020.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Nikita Tropin <ntropin@yandex.ru>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {HttpClient} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {catchError, map, shareReplay, take} from 'rxjs/operators';
import {Environment} from '../../interfaces/Environment';
import {VideoPlatformConfig} from './video-platforms';
import {VideoInfo, VideoPlatformId, VideoRow} from './video-types';
import {YouTubePlatform} from './platforms/youtube-platform';
import {RutubePlatform} from './platforms/rutube-platform';
import {VkPlatform} from './platforms/vk-platform';

@Injectable({
  providedIn: 'root'
})
export class VideoService {
  private readonly videoInfoCache = new Map<string, Observable<VideoInfo>>();
  private readonly environment = inject(Environment);

  private readonly platforms: { [key: string]: VideoPlatformConfig } = {
    youtube: new YouTubePlatform(),
    rutube: new RutubePlatform(),
    vk: new VkPlatform(this.environment.serverApi)
  };

  constructor(private readonly http: HttpClient) {
  }

  getPlatform(row: VideoRow): VideoPlatformConfig {
    if (!row?.link) {
      return this.platforms[VideoPlatformId.YOUTUBE];
    }

    const found = Object.values(this.platforms).find(config =>
      config.patterns.some(pattern => pattern.test(row.link))
    );

    return found || this.platforms[VideoPlatformId.YOUTUBE];
  }

  getVideoUrl(row: VideoRow): string {
    if (row.link) return row.link;
    // legacy: записи только с code всегда YouTube (другие платформы имеют link)
    if (row.code) return `https://www.youtube.com/watch?v=${row.code}`;
    return '';
  }

  getVideoPreview(row: VideoRow): Observable<string> {
    if (!row) {
      return of('');
    }

    const platform = this.getPlatform(row);
    const preview = platform.getPreview(row);
    return preview
      ? of(preview)
      : this.getVideoInfo(row).pipe(map(r => r.preview));
  }


  getVideoInfo(row: VideoRow): Observable<VideoInfo> {
    if (!row) {
      return of(this.createEmptyVideoInfo());
    }

    const cacheKey = this.getCacheKey(row);
    if (!cacheKey) {
      return of(this.createEmptyVideoInfo());
    }

    const cached = this.videoInfoCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const platform = this.getPlatform(row);
    const defaultInfo = this.createDefaultVideoInfo(row);

    const result$ = platform.getInfo(row, this.http).pipe(
      take(1),
      catchError((err) => {
        console.error('Error fetching video info:', err);
        return of(defaultInfo);
      }),
      shareReplay({bufferSize: 1, refCount: true})
    );

    this.videoInfoCache.set(cacheKey, result$);
    return result$;
  }

  getPlatformExamples(): string[] {
    return Object.values(this.platforms).map(platform => platform.exampleLink);
  }

  isSupported(url: string): boolean {
    return Object.values(this.platforms).some(p =>
      p.patterns.some(pattern => pattern.test(url))
    );
  }

  private getCacheKey(row: VideoRow): string {
    return row.link || row.code || '';
  }

  private createEmptyVideoInfo(): VideoInfo {
    return {link: '', preview: '', title: ''};
  }

  private createDefaultVideoInfo(row: VideoRow): VideoInfo {
    return {
      link: row.link || '',
      preview: '',
      title: row.link || ''
    };
  }
}
